import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import {
  Home,
  Calendar as CalendarIcon,
  Clock,
  User,
  Plus,
  ChevronLeft,
  ChevronRight,
  Trash2,
  Video,
  Building2,
  Settings,
  Users,
  Mail,
  Phone,
  UserCircle,
} from "lucide-react";
import {
  format,
  startOfWeek,
  endOfWeek,
  addWeeks,
  subWeeks,
  eachDayOfInterval,
  isSameDay,
  parseISO,
  startOfMonth,
  endOfMonth,
  eachWeekOfInterval,
} from "date-fns";
import { fr } from "date-fns/locale";
import { ProNavigation } from "@/components/ProNavigation";
import { QuestionnaireViewer } from "@/components/booking/QuestionnaireViewer";
import { AppointmentLegend } from "@/components/pro/AppointmentLegend";

interface Appointment {
  id: string;
  patient_id: string;
  appointment_date: string;
  duration_minutes: number;
  status: string;
  notes: string;
  additional_notes?: string | null;
  service_type: string | null;
  is_online: boolean | null;
  event_id: string | null;
  meet_link: string | null;
  patient?: {
    name: string;
  };
  child_first_name?: string | null;
  welcome_questionnaires?: Array<{
    responses: Record<string, any>;
    questionnaire_type: string;
  }>;
}

interface EventRegistration {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
}

const SERVICE_COLORS = {
  "Naturopathie Adultes": "bg-[hsl(var(--primary))] text-primary-foreground",
  "Naturopathie Adultes - Bilan de vitalité": "bg-[hsl(var(--primary))] text-primary-foreground",
  "Naturopathie Adultes - Consultation de suivi": "bg-[hsl(var(--primary))] text-primary-foreground",
  "Naturopathie Enfants & Ados": "bg-orange-500 text-white",
  "Naturopathie Enfants & Ados - Bilan de vitalité": "bg-orange-500 text-white",
  "Naturopathie Enfants & Ados - Consultation de Suivi": "bg-orange-500 text-white",
  "Gestion du Stress & Sommeil": "bg-purple-600 text-white",
  "Approche Énergétique": "bg-yellow-600 text-white",
  "Iridologie": "bg-teal-600 text-white",
  "Bilan iridologique": "bg-teal-600 text-white",
  "Fleurs de Bach": "bg-pink-500 text-white",
  "Accompagnement en Fleurs de Bach": "bg-pink-500 text-white",
  "Accompagnement en Fleurs de Bach – Équilibre émotionnel": "bg-pink-500 text-white",
} as const;


interface Patient {
  id: string;
  name: string;
}

export default function Appointments() {
  const navigate = useNavigate();
  const [view, setView] = useState<"week" | "month">("week");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<{ date: Date; hour: number } | null>(null);
  const [editingAppointment, setEditingAppointment] = useState<Appointment | null>(null);
  const [showEventDetailsDialog, setShowEventDetailsDialog] = useState(false);
  const [selectedEventAppointment, setSelectedEventAppointment] = useState<Appointment | null>(null);
  const [eventRegistrations, setEventRegistrations] = useState<EventRegistration[]>([]);

  const [formData, setFormData] = useState({
    patient_id: "",
    duration_minutes: 60,
    notes: "",
    hour: 8,
    minute: 0,
  });
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    loadAppointments();
    loadPatients();
  }, [currentDate, view]);

  // Update current time every minute for the blue line
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  const loadAppointments = async () => {
    setLoading(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      const startDate = view === "week" ? startOfWeek(currentDate, { locale: fr }) : startOfMonth(currentDate);
      const endDate = view === "week" ? endOfWeek(currentDate, { locale: fr }) : endOfMonth(currentDate);

      const { data, error } = await (supabase as any)
        .from("appointments")
        .select(
          `
          *,
          patient:patients(name),
          welcome_questionnaires(responses, questionnaire_type)
        `,
        )
        .eq("practitioner_id", user.id)
        .gte("appointment_date", startDate.toISOString())
        .lte("appointment_date", endDate.toISOString())
        .order("appointment_date");

      if (error) throw error;
      
      // Extract child_first_name from welcome_questionnaires
      const appointmentsWithChildName = (data || []).map((apt: any) => ({
        ...apt,
        child_first_name: apt.welcome_questionnaires?.[0]?.responses?.prenom_enfant || null,
      }));
      
      setAppointments(appointmentsWithChildName);
    } catch (error: any) {
      toast.error("Erreur lors du chargement des rendez-vous", {
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const loadPatients = async () => {
    try {
      const { data, error } = await supabase.from("patients").select("id, name").order("name");

      if (error) throw error;
      setPatients(data || []);
    } catch (error: any) {
      toast.error("Erreur lors du chargement des patients");
    }
  };

  const handleCreateAppointment = async () => {
    if (!selectedSlot || !formData.patient_id) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      const appointmentDate = new Date(selectedSlot.date);
      appointmentDate.setHours(formData.hour, formData.minute, 0, 0);

      if (editingAppointment) {
        // Update existing appointment
        const { error } = await (supabase as any)
          .from("appointments")
          .update({
            patient_id: formData.patient_id,
            appointment_date: appointmentDate.toISOString(),
            duration_minutes: formData.duration_minutes,
            notes: formData.notes,
          })
          .eq("id", editingAppointment.id);

        if (error) throw error;
        toast.success("Rendez-vous modifié avec succès");
      } else {
        // Create new appointment
        const { error } = await (supabase as any).from("appointments").insert({
          patient_id: formData.patient_id,
          practitioner_id: user.id,
          appointment_date: appointmentDate.toISOString(),
          duration_minutes: formData.duration_minutes,
          notes: formData.notes,
          status: "scheduled",
        });

        if (error) throw error;
        toast.success("Rendez-vous créé avec succès");
      }

      setShowDialog(false);
      setFormData({ patient_id: "", duration_minutes: 60, notes: "", hour: 8, minute: 0 });
      setSelectedSlot(null);
      setEditingAppointment(null);
      loadAppointments();
    } catch (error: any) {
      toast.error("Erreur lors de la sauvegarde du rendez-vous", {
        description: error.message,
      });
    }
  };

  const openEditDialog = async (appointment: Appointment) => {
    // Si c'est un événement, afficher les participants
    if (appointment.event_id) {
      setSelectedEventAppointment(appointment);
      setEventRegistrations([]); // Réinitialiser avant de charger
      await loadEventRegistrations(appointment.event_id);
      setShowEventDetailsDialog(true);
      return;
    }

    const aptDate = parseISO(appointment.appointment_date);
    setSelectedSlot({ date: aptDate, hour: aptDate.getHours() });
    setEditingAppointment(appointment);
    setFormData({
      patient_id: appointment.patient_id,
      duration_minutes: appointment.duration_minutes,
      notes: appointment.notes || "",
      hour: aptDate.getHours(),
      minute: aptDate.getMinutes(),
    });
    setShowDialog(true);
  };

  const loadEventRegistrations = async (eventId: string) => {
    try {
      const { data, error } = await supabase
        .from("event_registrations")
        .select("*")
        .eq("event_id", eventId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setEventRegistrations(data || []);
    } catch (error: any) {
      toast.error("Erreur lors du chargement des participants", {
        description: error.message,
      });
    }
  };

  const getAppointmentsForSlot = (date: Date, hour: number) => {
    return appointments.filter((apt) => {
      const aptDate = parseISO(apt.appointment_date);
      const aptStartHour = aptDate.getHours();
      const aptEndHour = aptStartHour + Math.ceil(apt.duration_minutes / 60);

      // Check if this hour slot is within the appointment duration
      return isSameDay(aptDate, date) && hour >= aptStartHour && hour < aptEndHour;
    });
  };

  const isAppointmentStart = (apt: Appointment, hour: number) => {
    const aptDate = parseISO(apt.appointment_date);
    return aptDate.getHours() === hour;
  };

  const deleteAppointment = async (appointmentId: string) => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer ce rendez-vous ?")) {
      return;
    }

    try {
      const { error } = await (supabase as any).from("appointments").delete().eq("id", appointmentId);

      if (error) throw error;

      toast.success("Rendez-vous supprimé avec succès");
      loadAppointments();
    } catch (error: any) {
      toast.error("Erreur lors de la suppression", {
        description: error.message,
      });
    }
  };

  const hours = Array.from({ length: 13 }, (_, i) => i + 8); // 8h à 20h
  const timeSlots = hours.flatMap((h) => [
    { hour: h, minute: 0, label: `${h}:00` },
    { hour: h, minute: 30, label: `${h}:30` },
  ]); // Créneaux de 30 minutes

  const renderWeekView = () => {
    const weekStart = startOfWeek(currentDate, { locale: fr, weekStartsOn: 1 });
    const weekEnd = endOfWeek(currentDate, { locale: fr, weekStartsOn: 1 });
    const days = eachDayOfInterval({ start: weekStart, end: weekEnd });

    // Calculate current time line position using state for real-time updates
    const currentHour = currentTime.getHours();
    const currentMinute = currentTime.getMinutes();
    const currentTotalMinutes = currentHour * 60 + currentMinute;
    const startMinutes = 8 * 60; // 8:00
    const endMinutes = 20 * 60; // 20:00
    const showTimeLine = currentTotalMinutes >= startMinutes && currentTotalMinutes <= endMinutes;

    // Position calculation with +28px offset to align with grid (30px - 2px adjustment)
    const minutesFromStart = currentTotalMinutes - startMinutes;
    const timeLinePosition = (minutesFromStart / 30) * 30 + 28;

    return (
      <div className="overflow-x-auto -mx-4 sm:mx-0">
        <div className="min-w-[900px] px-4 sm:px-0">
          {/* Header with days */}
          <div className="grid grid-cols-8 gap-0 mb-0 sticky top-0 bg-background z-30">
            <div className="p-2 font-semibold text-center border-r border-b border-border bg-muted">Horaires</div>
            {days.map((day) => (
              <div
                key={day.toISOString()}
                className="p-2 font-semibold text-center bg-muted border-r border-b border-border"
              >
                <div className="text-sm">{format(day, "EEE", { locale: fr })}</div>
                <div className="text-lg font-bold">{format(day, "d", { locale: fr })}</div>
              </div>
            ))}
          </div>

          {/* Calendar grid with 30-minute slots */}
          <div className="relative">
            {timeSlots.map((slot, index) => (
              <div key={`${slot.hour}-${slot.minute}`} className="grid grid-cols-8 gap-0">
                {/* Time label column */}
                <div
                  className={`p-1 text-right text-xs text-muted-foreground border-r border-border h-[30px] flex items-start justify-end pr-2 ${
                    slot.minute === 0 ? "font-semibold" : ""
                  }`}
                >
                  {slot.minute === 0 ? slot.label : ""}
                </div>

                {/* Day columns */}
                {days.map((day, dayIndex) => {
                  // La cellule représente le créneau qui COMMENCE à cette ligne
                  const actualSlot = index > 0 ? timeSlots[index - 1] : slot;

                  return (
                    <button
                      key={`${day.toISOString()}-${slot.hour}-${slot.minute}`}
                      onClick={() => {
                        setSelectedSlot({ date: day, hour: actualSlot.hour });
                        setEditingAppointment(null);
                        setFormData({
                          patient_id: "",
                          duration_minutes: 60,
                          notes: "",
                          hour: actualSlot.hour,
                          minute: actualSlot.minute,
                        });
                        setShowDialog(true);
                      }}
                      className={`h-[30px] border-r border-border hover:bg-accent/50 transition-colors ${
                        slot.minute === 0 ? "border-b" : "border-b border-dashed border-border/50"
                      }`}
                    />
                  );
                })}
              </div>
            ))}

            {/* Current time line */}
            {showTimeLine && (
              <div
                className="absolute left-0 right-0 pointer-events-none z-20"
                style={{ top: `${timeLinePosition}px` }}
              >
                <div className="flex items-center">
                  <div className="w-[calc(100%/8)] flex justify-end pr-1">
                    <div className="w-3 h-3 rounded-full bg-blue-500 shadow-lg" />
                  </div>
                  <div className="flex-1 h-[2px] bg-blue-500 shadow-sm" />
                </div>
              </div>
            )}

            {/* Appointments overlay - positioned in columns */}
            {days.map((day, dayIndex) => {
              const dayAppointments = appointments.filter((apt) => isSameDay(parseISO(apt.appointment_date), day));

              return (
                <div
                  key={`apt-col-${dayIndex}`}
                  className="absolute pointer-events-none"
                  style={{
                    left: `calc(${((dayIndex + 1) / 8) * 100}% + 1px)`,
                    width: `calc(${(1 / 8) * 100}% - 2px)`,
                    top: 0,
                    bottom: 0,
                  }}
                >
                  {dayAppointments.map((apt) => {
                    const aptDate = parseISO(apt.appointment_date);
                    const startHour = aptDate.getHours();
                    const startMinute = aptDate.getMinutes();

                    // Find the exact slot index in the timeSlots array
                    const slotIndex = timeSlots.findIndex(
                      (slot) => slot.hour === startHour && slot.minute === startMinute,
                    );

                    // Position: (slot index × 30px) + 30px offset to align with grid
                    const topPosition = slotIndex >= 0 ? slotIndex * 30 + 30 : 30;

                    // Calculate height: duration in 30-min increments
                    const heightInPixels = (apt.duration_minutes / 30) * 30;

                    return (
                      <div
                        key={apt.id}
                        onClick={() => openEditDialog(apt)}
                        className={`absolute pointer-events-auto group cursor-pointer ${getServiceColor(
                          apt.service_type,
                        )} rounded px-1 py-0.5 shadow-sm border-l-4 border-l-white/30 overflow-hidden hover:shadow-md hover:z-30 transition-all text-xs`}
                        style={{
                          top: `${topPosition}px`,
                          height: `${heightInPixels - 1}px`,
                          left: "2px",
                          right: "2px",
                          zIndex: 15,
                        }}
                      >
                        <div className="font-semibold truncate leading-tight">
                          {apt.patient?.name}
                          {apt.child_first_name && (
                            <span className="opacity-80"> ({apt.child_first_name})</span>
                          )}
                        </div>
                        <div className="opacity-90 truncate leading-tight">
                          {String(startHour).padStart(2, "0")}:{String(startMinute).padStart(2, "0")}
                        </div>
                        {heightInPixels > 40 && apt.notes && (
                          <div className="opacity-75 truncate text-[10px] mt-0.5">{apt.notes}</div>
                        )}
                        {/* Consultant file icon - bottom left */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/patients/${apt.patient_id}`);
                          }}
                          className="absolute bottom-0.5 left-0.5 bg-white/90 rounded p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Voir la fiche consultant"
                        >
                          <UserCircle className="w-3 h-3 text-primary" />
                        </button>
                        {/* Online/Office icon */}
                        <div className="absolute bottom-0.5 right-0.5 flex items-center gap-0.5">
                          {apt.is_online && apt.meet_link && (
                            <a
                              href={apt.meet_link}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="bg-white/90 rounded p-0.5"
                              title="Rejoindre Google Meet"
                            >
                              <Video className="w-3 h-3 text-blue-600" />
                            </a>
                          )}
                          {apt.is_online && !apt.meet_link && (
                            <div className="bg-white/90 rounded p-0.5" title="Consultation en visio">
                              <Video className="w-3 h-3 text-blue-600" />
                            </div>
                          )}
                          {!apt.is_online && (
                            <div className="bg-white/90 rounded p-0.5" title="Consultation au cabinet">
                              <Building2 className="w-3 h-3 text-green-700" />
                            </div>
                          )}
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteAppointment(apt.id);
                          }}
                          className="absolute top-0.5 right-0.5 p-0.5 rounded bg-destructive text-destructive-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                          title="Supprimer"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderMonthView = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const weeks = eachWeekOfInterval({ start: monthStart, end: monthEnd }, { locale: fr, weekStartsOn: 1 });

    return (
      <div className="grid grid-cols-7 gap-2">
        {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map((day) => (
          <div key={day} className="p-2 font-semibold text-center">
            {day}
          </div>
        ))}
        {weeks.map((weekStart) => {
          const days = eachDayOfInterval({
            start: weekStart,
            end: endOfWeek(weekStart, { locale: fr, weekStartsOn: 1 }),
          });
          return days.map((day) => {
            const dayAppointments = appointments.filter((apt) => isSameDay(parseISO(apt.appointment_date), day));
            const isCurrentMonth = day.getMonth() === currentDate.getMonth();

            return (
              <div
                key={day.toISOString()}
                className={`min-h-[100px] p-2 border border-border rounded-lg ${
                  isCurrentMonth ? "bg-background" : "bg-muted/50"
                }`}
              >
                <div
                  className={`text-sm font-medium mb-1 ${isCurrentMonth ? "text-foreground" : "text-muted-foreground"}`}
                >
                  {format(day, "d")}
                </div>
                <div className="space-y-1">
                  {dayAppointments.slice(0, 3).map((apt) => (
                    <div
                      key={apt.id}
                      onClick={() => openEditDialog(apt)}
                      className={`group text-xs p-1 rounded cursor-pointer hover:opacity-80 transition-opacity flex items-center gap-1 ${getServiceColor(apt.service_type)}`}
                    >
                      <span className="truncate flex-1">
                        {format(parseISO(apt.appointment_date), "HH:mm")} - {apt.patient?.name}
                        {apt.child_first_name && <span className="opacity-80"> ({apt.child_first_name})</span>}
                      </span>
                      <button
                        type="button"
                        aria-label="Supprimer le rendez-vous"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteAppointment(apt.id);
                        }}
                        className="opacity-0 group-hover:opacity-100 hover:bg-black/20 rounded p-0.5 transition-opacity shrink-0"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  {dayAppointments.length > 3 && (
                    <div className="text-xs text-muted-foreground">+{dayAppointments.length - 3} autres</div>
                  )}
                </div>
              </div>
            );
          });
        })}
      </div>
    );
  };

  const navigatePrevious = () => {
    setCurrentDate((prev) =>
      view === "week" ? subWeeks(prev, 1) : new Date(prev.getFullYear(), prev.getMonth() - 1, 1),
    );
  };

  const navigateNext = () => {
    setCurrentDate((prev) =>
      view === "week" ? addWeeks(prev, 1) : new Date(prev.getFullYear(), prev.getMonth() + 1, 1),
    );
  };

  const getServiceColor = (serviceType: string | null) => {
    if (!serviceType) return "bg-primary text-primary-foreground";
    return SERVICE_COLORS[serviceType as keyof typeof SERVICE_COLORS] || "bg-primary text-primary-foreground";
  };

  return (
    <div className="min-h-screen bg-gradient-hero">
      <div className="container mx-auto p-4 md:p-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-2 sm:gap-4">
            <Button variant="outline" onClick={() => navigate("/dashboard")} className="shrink-0">
              <Home className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Accueil</span>
            </Button>
            <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
              <CalendarIcon className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
              Rendez-vous
            </h1>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
            <Button variant="outline" onClick={() => navigate("/availability-settings")} className="flex-1 sm:flex-initial text-xs sm:text-sm">
              <Settings className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Horaires d'ouverture</span>
              <span className="sm:hidden">Horaires</span>
            </Button>
          </div>
        </div>

        {/* Legend for appointment colors (auto-generated from active accompaniments) */}
        <AppointmentLegend />

        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-2 sm:gap-4 overflow-x-auto pb-2 sm:pb-0">
                <Button variant="outline" size="icon" onClick={navigatePrevious} className="shrink-0">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <CardTitle className="text-base sm:text-xl whitespace-nowrap">
                  {view === "week"
                    ? `Semaine du ${format(startOfWeek(currentDate, { locale: fr, weekStartsOn: 1 }), "dd MMM yyyy", { locale: fr })}`
                    : format(currentDate, "MMMM yyyy", { locale: fr })}
                </CardTitle>
                <Button variant="outline" size="icon" onClick={navigateNext} className="shrink-0">
                  <ChevronRight className="w-4 h-4" />
                </Button>
                <Button variant="outline" onClick={() => setCurrentDate(new Date())} className="shrink-0 text-xs sm:text-sm">
                  Aujourd'hui
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <Button variant={view === "week" ? "default" : "outline"} onClick={() => setView("week")} className="flex-1 sm:flex-initial text-xs sm:text-sm">
                  Semaine
                </Button>
                <Button variant={view === "month" ? "default" : "outline"} onClick={() => setView("month")} className="flex-1 sm:flex-initial text-xs sm:text-sm">
                  Mois
                </Button>
              </div>
            </div>
            <CardDescription className="text-xs sm:text-sm">Gérez vos rendez-vous avec vos consultants</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">Chargement...</div>
            ) : (
              <>{view === "week" ? renderWeekView() : renderMonthView()}</>
            )}
          </CardContent>
        </Card>

        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-lg sm:text-xl">{editingAppointment ? "Modifier le rendez-vous" : "Nouveau rendez-vous"}</DialogTitle>
              <DialogDescription className="text-xs sm:text-sm">
                {selectedSlot && `${format(selectedSlot.date, "EEEE dd MMMM yyyy", { locale: fr })}`}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Consultant</Label>
                <Select
                  value={formData.patient_id}
                  onValueChange={(value) => setFormData({ ...formData, patient_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un consultant" />
                  </SelectTrigger>
                  <SelectContent>
                    {patients.map((patient) => (
                      <SelectItem key={patient.id} value={patient.id}>
                        {patient.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Heure de début</Label>
                <Select
                  value={`${formData.hour}:${formData.minute}`}
                  onValueChange={(value) => {
                    const [h, m] = value.split(":").map(Number);
                    setFormData({ ...formData, hour: h, minute: m });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 13 }, (_, i) => i + 8)
                      .flatMap((h) => [
                        { hour: h, minute: 0 },
                        { hour: h, minute: 15 },
                        { hour: h, minute: 30 },
                        { hour: h, minute: 45 },
                      ])
                      .map((time) => (
                        <SelectItem key={`${time.hour}:${time.minute}`} value={`${time.hour}:${time.minute}`}>
                          {String(time.hour).padStart(2, "0")}:{String(time.minute).padStart(2, "0")}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Durée</Label>
                <Select
                  value={(formData.duration_minutes || 60).toString()}
                  onValueChange={(value) => setFormData({ ...formData, duration_minutes: parseInt(value) })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choisir une durée" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="30">30 minutes</SelectItem>
                    <SelectItem value="45">45 minutes</SelectItem>
                    <SelectItem value="60">1 heure</SelectItem>
                    <SelectItem value="75">1h15</SelectItem>
                    <SelectItem value="90">1h30</SelectItem>
                    <SelectItem value="120">2 heures</SelectItem>
                    <SelectItem value="150">2h30</SelectItem>
                    <SelectItem value="180">3 heures</SelectItem>
                    <SelectItem value="210">3h30</SelectItem>
                    <SelectItem value="240">4 heures</SelectItem>
                    {formData.duration_minutes && ![30,45,60,75,90,120,150,180,210,240].includes(formData.duration_minutes) && (
                      <SelectItem value={formData.duration_minutes.toString()}>{formData.duration_minutes} minutes</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Notes complémentaires..."
                  rows={3}
                />
              </div>
              {/* Google Meet link display for visio appointments */}
              {editingAppointment?.is_online && editingAppointment?.meet_link && (
                <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
                  <Label className="text-sm font-semibold text-blue-700 mb-1 block">Lien Google Meet</Label>
                  <a
                    href={editingAppointment.meet_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:underline break-all flex items-center gap-2"
                  >
                    <Video className="w-4 h-4 shrink-0" />
                    {editingAppointment.meet_link}
                  </a>
                </div>
              )}

              {/* Welcome questionnaire viewer */}
              {editingAppointment?.welcome_questionnaires && editingAppointment.welcome_questionnaires.length > 0 && (
                <QuestionnaireViewer
                  questionnaireType={editingAppointment.welcome_questionnaires[0].questionnaire_type}
                  responses={editingAppointment.welcome_questionnaires[0].responses || {}}
                  additionalNotes={editingAppointment.additional_notes}
                />
              )}
              <div className="flex flex-col sm:flex-row gap-2">
                <Button onClick={handleCreateAppointment} className="flex-1">
                  <Plus className="w-4 h-4 mr-2" />
                  {editingAppointment ? "Modifier le rendez-vous" : "Créer le rendez-vous"}
                </Button>
                {editingAppointment && (
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => {
                      const id = editingAppointment.id;
                      setShowDialog(false);
                      deleteAppointment(id);
                    }}
                    aria-label="Supprimer le rendez-vous"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Supprimer
                  </Button>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Dialog for Event Details */}
        <Dialog open={showEventDetailsDialog} onOpenChange={(open) => {
          setShowEventDetailsDialog(open);
          if (!open) {
            setEventRegistrations([]);
            setSelectedEventAppointment(null);
          }
        }}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Détails de l'événement</DialogTitle>
              <DialogDescription>
                Informations sur l'événement et liste des participants inscrits
              </DialogDescription>
            </DialogHeader>
            
            {selectedEventAppointment && (
              <div className="space-y-6">
                {/* Event info */}
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <CalendarIcon className="w-5 h-5 text-primary mt-0.5" />
                    <div>
                      <p className="font-semibold text-lg">{selectedEventAppointment.service_type}</p>
                      <p className="text-sm text-muted-foreground">
                        {format(parseISO(selectedEventAppointment.appointment_date), "EEEE d MMMM yyyy 'à' HH:mm", { locale: fr })}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-primary" />
                    <p className="text-sm">Durée: {selectedEventAppointment.duration_minutes} minutes</p>
                  </div>

                  {selectedEventAppointment.is_online ? (
                    <div className="flex items-center gap-3">
                      <Video className="w-5 h-5 text-blue-600" />
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                        En ligne / Visio
                      </Badge>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3">
                      <Building2 className="w-5 h-5 text-green-700" />
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                        Présentiel au cabinet
                      </Badge>
                    </div>
                  )}
                </div>

                {/* Registrations count */}
                <div className="bg-primary/10 p-4 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-primary" />
                    <p className="font-semibold text-primary">
                      {eventRegistrations.length} participant{eventRegistrations.length > 1 ? 's' : ''} inscrit{eventRegistrations.length > 1 ? 's' : ''}
                    </p>
                  </div>
                </div>

                {/* Participants list */}
                {eventRegistrations.length > 0 ? (
                  <div className="space-y-3">
                    <h3 className="font-semibold text-sm text-muted-foreground uppercase">
                      Liste des participants
                    </h3>
                    <div className="space-y-2">
                      {eventRegistrations.map((registration) => (
                        <Card key={registration.id} className="p-4">
                          <div className="space-y-2">
                            <p className="font-semibold text-foreground">
                              {registration.first_name} {registration.last_name}
                            </p>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Mail className="w-4 h-4" />
                              <a href={`mailto:${registration.email}`} className="hover:text-primary">
                                {registration.email}
                              </a>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Phone className="w-4 h-4" />
                              <a href={`tel:${registration.phone}`} className="hover:text-primary">
                                {registration.phone}
                              </a>
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Aucun participant inscrit pour le moment</p>
                  </div>
                )}

                <div className="flex justify-end gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => setShowEventDetailsDialog(false)}
                  >
                    Fermer
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
